/**
 * Back navigation that never pops the history stack.
 *
 * `history.goBack()` is not safe inside MLI's WebView: popping took the view
 * out of our SPA document entirely — the screen went blank and even a plain
 * setInterval stopped running, so no JS of ours was alive to recover. It is not
 * an Ionic or React problem; the document is simply gone.
 *
 * So every back button navigates FORWARD to an explicit route instead. Two
 * pieces:
 *
 *   withFrom(location, state)  - record the current screen (path AND its route
 *                                state) when pushing, so the destination knows
 *                                where it was opened from and can restore it.
 *
 *   goBackTo(history, routeLocation, fallback, state)
 *                              - navigate to that recorded screen, or to a
 *                                sensible fallback when a screen was reached
 *                                by a path that did not record one.
 *
 * Pushing rather than popping does mean the history stack grows. That is the
 * deliberate trade: a stack that grows is invisible to the user, a blank screen
 * is not.
 *
 * A trail entry is { path, state }. It used to be just the path string, which
 * is what caused the "heading and footer but no content" screens: /select-mode,
 * /providerCard and /select-location all read their service out of
 * location.state, so arriving there with a bare path rendered the chrome around
 * an empty middle. Carrying the state back with the path fixes that class of
 * bug at the source instead of one redux fallback per screen. Plain strings are
 * still accepted so that a state object pushed by an older build — still sitting
 * in the user's history — does not break on the way back.
 */

// The trail is carried through history.pushState, which structured-clones it on
// every navigation. Cap it so a long session cannot grow it without bound.
const MAX_TRAIL = 12;

function normalise(entry) {
  if (!entry) return null;
  return typeof entry === "string" ? { path: entry, state: {} } : entry;
}

// Two routes are the same view when their pathnames match; the query string is
// part of the URL but not of Ionic's view identity.
function samePage(path, pathname) {
  if (!path) return false;
  return String(path).split("?")[0] === pathname;
}

function entryFor(location) {
  const path = `${location.pathname || ""}${location.search || ""}`;
  // Strip the bookkeeping keys before storing: without this, every entry would
  // contain the whole trail that preceded it and the state object would grow
  // exponentially with depth. `direction` goes too — it describes ONE
  // navigation, and replaying it later would animate a forward move backwards
  // and unmount the wrong view.
  const { trail, from, direction, ...own } = location.state || {};
  return { path, state: own };
}

export function withFrom(location, state = {}) {
  if (!location) return state;
  const entry = entryFor(location);
  const previous = (location.state?.trail || []).map(normalise).filter(Boolean);
  const trail = [...previous, entry].slice(-MAX_TRAIL);
  return { ...state, from: entry.path, trail };
}

export function goBackTo(history, routeLocation, fallback, state) {
  // NOTE: delegating to Ionic's own back (router.goBack()) was tried and
  // reverted. Worse, passing the router in as an extra argument landed it in
  // `state`, and history.pushState cannot structured-clone an object holding
  // functions — every call threw DataCloneError and the back buttons silently
  // did nothing. Keep this signature: `state` must stay serialisable.
  let trail = (routeLocation?.state?.trail || [])
    .map(normalise)
    .filter(Boolean);
  const here = `${routeLocation?.pathname || ""}${routeLocation?.search || ""}`;
  const herePath = routeLocation?.pathname || "";
  // Drop trailing entries that are this same screen. A screen that pushes
  // itself (re-entering /select-location to change mode, say) records its own
  // path, and "back" then resolves to where we already are — history.push to
  // the current route does not switch the view, so the button appears dead.
  // Compare PATHNAMES, not full paths. Ionic switches views per route, so
  // pushing "/select-location" while already on "/select-location?x=1" is a new
  // URL but the same view — it does not transition, and the button looks dead.
  // Matching on the full string missed exactly that case.
  while (trail.length && samePage(trail[trail.length - 1].path, herePath)) {
    trail.pop();
  }
  const last = trail.length ? trail[trail.length - 1] : null;
  let target = last ? last.path : routeLocation?.state?.from || fallback;
  if (samePage(target, herePath)) target = fallback;
  if (!target || samePage(target, herePath)) return;

  // Hand the remaining trail to the destination so ITS back button knows where
  // to go — that chaining is what keeps a multi-step back on the right path.
  const remaining = trail.slice(0, -1);
  const previous = remaining.length ? remaining[remaining.length - 1] : null;

  // Restore the destination's own state first, then let an explicit argument
  // from the caller override it: callers that pass state are correcting for
  // something they know about the screen they are leaving.
  const nextState = {
    ...(last?.state || {}),
    ...(state || {}),
    trail: remaining,
    from: previous ? previous.path : undefined,
    // THIS is what makes the view actually change. @ionic/react-router reads
    // `location.state.direction` for a plain history.push:
    //
    //   routeDirection: location.state?.direction || 'forward'
    //
    // and then, in StackManager.handlePageTransition:
    //
    //   if (!(routeAction === 'push' && routeDirection === 'forward')) {
    //     if (routeDirection !== 'none' && entering !== leaving)
    //       leavingViewItem.mount = false;
    //   }
    //
    // So a push that defaults to 'forward' deliberately KEEPS the page we are
    // leaving mounted — which is why back moved the URL while the old screen
    // stayed on screen (/select-address still painted under a /bookingFor URL).
    // Declaring 'back' unmounts the leaving view and runs a back transition,
    // while we keep pushing rather than popping, so the WebView never leaves
    // our SPA document. Stripped from trail entries by entryFor().
    // REVERTED. Declaring a back direction DOES make Ionic switch the view —
    // but the same rule also sets `leavingViewItem.mount = false`, unmounting
    // the page we are leaving. On localhost the destination remounts and
    // registers its IonPage fast enough that nobody sees it; inside MLI's
    // WebView it does not, and back from saved addresses went BLANK.
    // handlePageTransition only transitions when the entering view already
    // has an ionPageElement — if it does not, nothing is committed and we
    // have just unmounted the only page on screen.
    //
    // Stale UI is bad; blank is worse, and blank is what this whole file
    // exists to avoid. The real bug is that the entering view often has no
    // usable ionPageElement — see the nested-IonPage note in the header.
    // direction: "back",
  };

  // push, not replace.
  //
  // This is the mechanism verified working on a device ("back button is working
  // now on saved addresses b9"). It was later switched to replace to stop a
  // services <-> booking-for ping-pong, but replace was never re-verified for
  // the in-page back buttons and it does not reliably switch the view: Ionic
  // treats it as route action 'replace' with direction 'none', and replacing to
  // a route already in its stack can leave the current view on screen — which
  // presents as "the back button does nothing".
  //
  // The known cost: the stack grows, so the entry behind the destination is
  // still the screen we just left. That only matters for a POP (the device or
  // gesture back), which is a separate open issue.
  history.push(target, nextState);
}

/**
 * Back to a KNOWN parent screen, carrying the trail with it.
 *
 * Use this instead of a bare `history.push(parent, {...})` when a screen knows
 * exactly which screen sits behind it and does not want to follow the trail
 * blindly. A bare push is what broke multi-step back: it hands the parent a
 * state object with no `trail`, so the parent's own back button has nothing
 * left to resolve and drops to its fallback — a bare "/navigo" with no query
 * string, which is not the screen an autologin deep link opened
 * (/navigo?currScreen=1&pageType=7 and friends).
 *
 * The destination is forced, exactly as a hard-coded push would; what changes
 * is that the trail recorded on the way in is rewound to that screen rather
 * than thrown away. If the trail happens to hold the parent WITH its query
 * string, that recorded path wins over the bare `path` argument — that is how
 * the autologin entry URL survives the trip back.
 */
export function backToPath(history, routeLocation, path, state) {
  const trail = (routeLocation?.state?.trail || [])
    .map(normalise)
    .filter(Boolean);
  const wanted = String(path).split("?")[0];

  // Search from the end: a screen can appear more than once in a long session
  // and the nearest one is the one we came through.
  let index = -1;
  for (let i = trail.length - 1; i >= 0; i--) {
    if (samePage(trail[i].path, wanted)) {
      index = i;
      break;
    }
  }

  const entry = index === -1 ? null : trail[index];
  const remaining = index === -1 ? [] : trail.slice(0, index);
  const previous = remaining.length ? remaining[remaining.length - 1] : null;

  history.push(entry ? entry.path : path, {
    ...(entry?.state || {}),
    ...(state || {}),
    trail: remaining,
    from: previous ? previous.path : undefined,
    // See goBackTo: without this Ionic treats the push as 'forward' and leaves
    // the screen we are leaving mounted and visible.
    // REVERTED. Declaring a back direction DOES make Ionic switch the view —
    // but the same rule also sets `leavingViewItem.mount = false`, unmounting
    // the page we are leaving. On localhost the destination remounts and
    // registers its IonPage fast enough that nobody sees it; inside MLI's
    // WebView it does not, and back from saved addresses went BLANK.
    // handlePageTransition only transitions when the entering view already
    // has an ionPageElement — if it does not, nothing is committed and we
    // have just unmounted the only page on screen.
    //
    // Stale UI is bad; blank is worse, and blank is what this whole file
    // exists to avoid. The real bug is that the entering view often has no
    // usable ionPageElement — see the nested-IonPage note in the header.
    // direction: "back",
  });
}
